import { Card } from '../../../ui';
import { usePatientContext } from '../../containers/PatientContainer/usePatientContext';
import { subThreeMonths, useChartData } from '../../hooks';
import { EmptyMessage, ChartContainer } from './styles';
import { useState, useRef, useEffect, FC, useMemo } from 'react';
import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
  Chip,
} from '@mui/material';
import { DateRangeModal } from '../DateRangePicker';
import { ChartsYAxisProps, LineSeriesType } from '@mui/x-charts';
import { CustomLineChart } from '../Chart/CustomLineChart';

const healthDataKeyToLabel: { [key: string]: string } = {
  steps: 'Steps',
  floors_climbed: 'Floors Climbed',
  double_support_time: 'Double Support Time',
  step_cadence: 'Step Cadence',
  walking_asymmetry: 'Walking Asymmetry',
  walking_speed: 'Walking Speed',
  walking_step_length: 'Walking Step Length',
};

const clinicalKeyToLabel: { [key: string]: string } = {
  forward_bend: 'Forward Bend',
  plumb_shift: 'Plumb Shift',
  predicted_cobb: 'Predicted Cobb Angle',
  shoulder_asymmetry: 'Shoulder Asymmetry',
  trunk_shift: 'Trunk Shift',
  waist_asymmetry: 'Waist Asymmetry',
};

const keyToLabel: { [key: string]: string } = {
  ...clinicalKeyToLabel,
  ...healthDataKeyToLabel,
} as const;

const ITEM_HEIGHT = 100;
const ITEM_PADDING_TOP = 0;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
export enum ButtonGroupOptions {
  THREE = 'three',
  YTD = 'ytd',
  ALL_TIME = 'all_time',
  CUSTOM = 'custom',
}

interface PatientProgressionCardProps {
  hasBraceSensorData?: boolean;
}

export const PatientProgressionCard: FC<PatientProgressionCardProps> = ({
  ...props
}) => {
  const { patient } = usePatientContext();
  const { hasBraceSensorData } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState<number>(0);
  const [hasSetInitialHeight, setHasSetInitialHeight] = useState(false);
  const { chartData, loading, setDateRange, dateRange } = useChartData(
    patient.id
  );

  const [selected, setSelected] = useState<ButtonGroupOptions>(
    ButtonGroupOptions.THREE
  );
  const handleButtonSelect = (value: ButtonGroupOptions) => {
    setSelected(value);
  };
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([
    'predicted_cobb',
  ]);
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);

  const handleDateRangeModalClose = () => {
    setShowDateRangeModal(false);
  };

  const chartYAxes: ChartsYAxisProps[] = useMemo(() => {
    return selectedMetrics.map((key, index) => {
      const isLeft = index === 0;

      const axisId = isLeft ? 'leftAxis' : 'rightAxis';

      const axisPosition = isLeft ? 'left' : 'right';

      let min = undefined;
      let max = undefined;

      if (key === 'predicted_cobb') {
        min = 0;
        max = 100;
      }

      return {
        id: axisId,
        position: axisPosition,
        disableTicks: true,
        label: keyToLabel[key],
        min,
        max,
      };
    });
  }, [selectedMetrics]);

  const chartSeries: LineSeriesType[] = useMemo(() => {
    const colors = ['blue', 'lightblue'];

    return selectedMetrics.map((key, index) => {
      const isLeft = index === 0;
      const yAxisId = isLeft ? 'leftAxis' : 'rightAxis';

      return {
        type: 'line',
        yAxisId,
        dataKey: key,
        label: keyToLabel[key],
        color: colors[index],
        connectNulls: true,
        valueFormatter: (value: any) => (value ? value.toString() : 'NA'),
      };
    });
  }, [selectedMetrics]);

  const handleMetricsChange = (
    event: SelectChangeEvent<typeof selectedMetrics>
  ) => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value.
    const selected = typeof value === 'string' ? value.split(',') : value;

    // Limit to maximum 2 selections
    if (selected.length <= 2) {
      setSelectedMetrics(selected);
    } else if (selected.length === 0) {
      setSelectedMetrics([selectedMetrics[0]]);
    }
  };

  const handleDateRangeSelect = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value as ButtonGroupOptions;

    handleButtonSelect(selectedValue);

    // Set the date range based on the selected option
    switch (selectedValue) {
      case ButtonGroupOptions.THREE:
        setDateRange({
          start: subThreeMonths(new Date()),
          end: new Date(),
        });
        break;
      case ButtonGroupOptions.YTD:
        setDateRange({
          start: new Date(new Date().getFullYear(), 0, 1),
          end: new Date(),
        });
        break;
      case ButtonGroupOptions.ALL_TIME:
        setDateRange({
          start: new Date(2023, 0, 1),
          end: new Date(),
        });
        break;
      case ButtonGroupOptions.CUSTOM:
        break;
      // Custom is handled separately with the DateRangeModal
    }
  };

  useEffect(() => {
    if (
      containerRef.current &&
      controlsRef.current &&
      !loading &&
      !hasSetInitialHeight
    ) {
      if (hasBraceSensorData) {
        const containerHeight = containerRef.current.clientHeight;
        const controlsHeight = controlsRef.current.clientHeight;
        const remainingHeight = containerHeight - controlsHeight;

        setHasSetInitialHeight(true);
        setChartHeight(remainingHeight);
      } else {
        setChartHeight(364);
      }
    }
  }, [
    containerRef,
    controlsRef,
    loading,
    hasSetInitialHeight,
    hasBraceSensorData,
  ]);

  return (
    <Card
      height='100%'
      loading={loading}
      title='Activity Dashboard'
      ref={containerRef}
    >
      <ControlsContainer ref={controlsRef}>
        <div>
          <FormControl sx={{ m: 1, maxWidth: '25vw' }}>
            <InputLabel id='metrics-select-label'>Metrics</InputLabel>
            <Select
              labelId='metrics-select-label'
              id='metrics-select'
              style={{
                fontSize: '0.75rem',
                height: '3rem',
              }}
              multiple
              value={selectedMetrics}
              onChange={handleMetricsChange}
              input={<Input />}
              renderValue={(selected) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip
                      key={value}
                      label={keyToLabel[value]}
                      style={{ fontSize: '0.75rem' }}
                      size='small'
                    />
                  ))}
                </div>
              )}
              MenuProps={MenuProps}
            >
              <ListSubheader>Clinical</ListSubheader>
              {Object.keys(clinicalKeyToLabel).map((key) => (
                <MenuItem
                  key={key}
                  value={key}
                  disabled={
                    (selectedMetrics.length >= 2 &&
                      !selectedMetrics.includes(key)) ||
                    (selectedMetrics.length === 1 && selectedMetrics[0] === key)
                  }
                >
                  <Checkbox
                    checked={selectedMetrics.includes(key)}
                    size='small'
                  />
                  <ListItemText primary={clinicalKeyToLabel[key]} />
                </MenuItem>
              ))}
              <ListSubheader>Health Data</ListSubheader>
              {Object.keys(healthDataKeyToLabel).map((key) => (
                <MenuItem
                  key={key}
                  value={key}
                  disabled={
                    (selectedMetrics.length >= 2 &&
                      !selectedMetrics.includes(key)) ||
                    (selectedMetrics.length === 1 && selectedMetrics[0] === key)
                  }
                >
                  <Checkbox
                    checked={selectedMetrics.includes(key)}
                    size='small'
                  />
                  <ListItemText primary={healthDataKeyToLabel[key]} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ m: 1, width: '14vw' }}>
            <InputLabel id='date-range-select-label'>Date Range</InputLabel>
            <Select
              labelId='date-range-select-label'
              id='date-range-select'
              style={{ fontSize: '0.75rem', height: '3rem' }}
              variant='standard'
              value={selected}
              onChange={handleDateRangeSelect}
            >
              <MenuItem value={ButtonGroupOptions.THREE}>
                Last 3 Months
              </MenuItem>
              <MenuItem value={ButtonGroupOptions.YTD}>Year to Date</MenuItem>
              <MenuItem value={ButtonGroupOptions.ALL_TIME}>All Time</MenuItem>
              <MenuItem
                value={ButtonGroupOptions.CUSTOM}
                onClick={() => {
                  setShowDateRangeModal(true);
                }}
              >
                Custom Range
              </MenuItem>
            </Select>
            <DateRangeModal
              open={showDateRangeModal}
              handleClose={handleDateRangeModalClose}
              start={dateRange.start}
              end={dateRange.end}
              onSubmit={(start, end) => setDateRange({ start, end })}
            />
          </FormControl>
        </div>
      </ControlsContainer>
      {chartData && chartData.length > 0 ? (
        <ChartContainer height={chartHeight}>
          <CustomLineChart
            chartData={chartData}
            series={chartSeries}
            yAxes={chartYAxes}
            xAxisRangeType={selected}
            xAxisRange={dateRange}
          />
        </ChartContainer>
      ) : (
        <EmptyMessage variant='bodyLarge'>
          No data for selected period.
        </EmptyMessage>
      )}
    </Card>
  );
};

const ControlsContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
`;
